import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Building2, 
  Users, 
  Award, 
  Heart,
  Target,
  Shield,
  TrendingUp,
  Star,
  MapPin,
  Calendar,
  Briefcase,
  Trophy,
  CheckCircle,
  ArrowRight,
  Globe,
  Handshake,
  DollarSign,
  GraduationCap
} from 'lucide-react';

const About = () => {
  const values = [
    {
      icon: Heart,
      title: 'Community First',
      description: 'We believe in supporting our local community and helping it thrive.'
    },
    {
      icon: Shield,
      title: 'Trust & Integrity',
      description: 'We operate with the highest ethical standards and transparency.'
    },
    {
      icon: Users,
      title: 'Personal Service',
      description: 'Every customer receives personalized attention and dedicated support.'
    },
    {
      icon: TrendingUp,
      title: 'Innovation',
      description: 'We embrace technology to provide modern, convenient banking solutions.'
    }
  ];

  const leadership = [
    {
      name: 'Robert Johnson',
      position: 'President & CEO',
      bio: '25+ years in community banking with expertise in strategic growth and customer service excellence.',
      image: 'https://images.unsplash.com/photo-1560250097-0b93528c311a?w=400&h=400&fit=crop&crop=face'
    },
    {
      name: 'Sarah Martinez',
      position: 'Chief Financial Officer',
      bio: 'Former regional banking executive with deep expertise in financial management and regulatory compliance.',
      image: 'https://images.unsplash.com/photo-1494790108755-2616b612b786?w=400&h=400&fit=crop&crop=face'
    },
    {
      name: 'Michael Chen',
      position: 'VP of Operations',
      bio: 'Technology leader focused on digital transformation and operational efficiency improvements.',
      image: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=400&h=400&fit=crop&crop=face'
    },
    {
      name: 'Jennifer Davis',
      position: 'VP of Lending',
      bio: 'Expert in commercial and residential lending with a passion for helping customers achieve their dreams.',
      image: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=400&h=400&fit=crop&crop=face'
    }
  ];

  const communityStats = [
    {
      icon: DollarSign,
      number: '$2.5M',
      label: 'Community Investment',
      description: 'Annual giving to local organizations'
    },
    {
      icon: Users,
      number: '150+',
      label: 'Local Partnerships',
      description: 'Businesses and nonprofits supported'
    },
    {
      icon: GraduationCap,
      number: '50+',
      label: 'Scholarships',
      description: 'Students supported annually'
    },
    {
      icon: Globe,
      number: '25',
      label: 'Locations',
      description: 'Serving communities across the region'
    }
  ];

  const awards = [
    {
      year: '2024',
              title: 'Best International Bank',
      organization: 'Regional Banking Association',
      description: 'Recognized for outstanding customer service and community support'
    },
    {
      year: '2023',
      title: 'Digital Innovation Award',
      organization: 'Banking Technology Institute',
      description: 'Leading digital transformation in community banking'
    },
    {
      year: '2023',
      title: 'Employer of Choice',
      organization: 'Local Business Journal',
      description: 'Best workplace culture and employee satisfaction'
    },
    {
      year: '2022',
      title: 'Community Impact Award',
      organization: 'Chamber of Commerce',
      description: 'Outstanding commitment to local economic development'
    }
  ];

  const careers = [
    {
      title: 'Personal Banker',
      department: 'Retail Banking',
      location: 'Multiple Locations',
      type: 'Full-time'
    },
    {
      title: 'Commercial Lending Officer',
      department: 'Commercial Banking',
      location: 'Downtown HQ',
      type: 'Full-time'
    },
    {
      title: 'IT Systems Analyst',
      department: 'Technology',
      location: 'Downtown HQ',
      type: 'Full-time'
    },
    {
      title: 'Customer Service Representative',
      department: 'Operations',
      location: 'Various Locations',
      type: 'Part-time/Full-time'
    }
  ];

  return (
    <div className="pt-20">
      {/* Hero Section */}
      <section className="relative py-20 lg:py-32 overflow-hidden bg-gradient-to-br from-primary-900 via-primary-800 to-primary-900">
        <div className="absolute inset-0 bg-gradient-to-r from-primary-900/90 to-primary-800/90"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center text-white"
          >
            <h1 className="text-4xl md:text-6xl font-bold leading-tight mb-6">
              Our Story
              <span className="block text-3xl md:text-5xl bg-gradient-to-r from-blue-400 to-cyan-300 bg-clip-text text-transparent">
                Since 1985
              </span>
            </h1>
            
            <p className="text-xl md:text-2xl text-blue-100 leading-relaxed max-w-3xl mx-auto mb-8">
                              For nearly four decades, First Capital International has been a cornerstone 
              of financial stability and growth in our community.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/contact"
                className="inline-flex items-center justify-center px-8 py-4 text-lg font-semibold text-primary-900 bg-white rounded-xl hover:bg-gray-50 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-1"
              >
                <Users className="w-6 h-6 mr-2" />
                Meet Our Team
              </Link>
              <Link
                to="/contact"
                className="inline-flex items-center justify-center px-8 py-4 text-lg font-semibold text-white border-2 border-white rounded-xl hover:bg-white hover:text-primary-900 transition-all duration-300"
              >
                Join Our Team
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Story Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                Built on Community Values
              </h2>
              <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                Founded in 1985 by local business leaders who believed their community deserved 
                better banking options, First Capital International has grown from a small 
                neighborhood institution to a trusted regional bank.
              </p>
              <p className="text-lg text-gray-600 leading-relaxed mb-6">
                We've maintained our commitment to personal service while embracing modern technology 
                to serve our customers better. Today, we're proud to serve over 50,000 customers 
                across 25 branch locations.
              </p>
              <div className="grid grid-cols-2 gap-6">
                <div className="text-center">
                  <div className="text-3xl font-bold text-primary-600">50,000+</div>
                  <div className="text-gray-600">Customers Served</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-primary-600">39</div>
                  <div className="text-gray-600">Years of Service</div>
                </div>
              </div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              className="relative"
            >
              <img
                src="https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=600&h=400&fit=crop"
                alt="Bank Building"
                className="rounded-2xl shadow-2xl"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              Our Core Values
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              These principles guide everything we do and shape how we serve our community.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <motion.div
                key={value.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="text-center group"
              >
                <div className="w-16 h-16 bg-gradient-to-r from-primary-600 to-primary-700 rounded-2xl flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform duration-300">
                  <value.icon className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">{value.title}</h3>
                <p className="text-gray-600 leading-relaxed">{value.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Leadership Team Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              Leadership Team
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
                              Meet the experienced professionals leading First Capital International into the future.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {leadership.map((leader, index) => (
              <motion.div
                key={leader.name}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="text-center group"
              >
                <div className="relative mb-6">
                  <img
                    src={leader.image}
                    alt={leader.name}
                    className="w-32 h-32 rounded-full mx-auto object-cover shadow-lg group-hover:shadow-xl transition-shadow duration-300"
                  />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">{leader.name}</h3>
                <div className="text-primary-600 font-semibold mb-3">{leader.position}</div>
                <p className="text-gray-600 text-sm leading-relaxed">{leader.bio}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Community Impact Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              Community Impact
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              We believe in giving back to the communities that have supported us for nearly four decades.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-16">
            {communityStats.map((stat, index) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="text-center"
              >
                <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <stat.icon className="w-8 h-8 text-primary-600" />
                </div>
                <div className="text-3xl font-bold text-primary-600 mb-2">{stat.number}</div>
                <div className="text-lg font-semibold text-gray-900 mb-1">{stat.label}</div>
                <div className="text-gray-600 text-sm">{stat.description}</div>
              </motion.div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <h3 className="text-2xl font-bold text-gray-900 mb-6">Our Community Initiatives</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="bg-white rounded-xl p-6 shadow-lg">
                <GraduationCap className="w-12 h-12 text-primary-600 mx-auto mb-4" />
                <h4 className="text-lg font-semibold text-gray-900 mb-2">Education Support</h4>
                <p className="text-gray-600">Annual scholarships and financial literacy programs for local students.</p>
              </div>
              <div className="bg-white rounded-xl p-6 shadow-lg">
                <Handshake className="w-12 h-12 text-primary-600 mx-auto mb-4" />
                <h4 className="text-lg font-semibold text-gray-900 mb-2">Local Business</h4>
                <p className="text-gray-600">Supporting local entrepreneurs with financing and mentorship programs.</p>
              </div>
              <div className="bg-white rounded-xl p-6 shadow-lg">
                <Heart className="w-12 h-12 text-primary-600 mx-auto mb-4" />
                <h4 className="text-lg font-semibold text-gray-900 mb-2">Nonprofit Partners</h4>
                <p className="text-gray-600">Partnering with local nonprofits to strengthen our community.</p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Awards & Recognition Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              Awards & Recognition
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Our commitment to excellence has been recognized by industry leaders and community organizations.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {awards.map((award, index) => (
              <motion.div
                key={award.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-gray-50 rounded-xl p-6 flex items-start space-x-4"
              >
                <div className="w-12 h-12 bg-primary-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <Trophy className="w-6 h-6 text-primary-600" />
                </div>
                <div>
                  <div className="text-sm text-primary-600 font-semibold mb-1">{award.year}</div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">{award.title}</h3>
                  <div className="text-gray-700 font-medium mb-2">{award.organization}</div>
                  <p className="text-gray-600">{award.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Careers Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              Join Our Team
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Build your career with a company that values growth, innovation, and community impact.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
            {careers.map((job, index) => (
              <motion.div
                key={job.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-white rounded-xl p-6 shadow-lg hover:shadow-xl transition-shadow duration-300"
              >
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <h3 className="text-lg font-bold text-gray-900 mb-1">{job.title}</h3>
                    <div className="text-primary-600 font-medium">{job.department}</div>
                  </div>
                  <span className="bg-primary-100 text-primary-600 px-3 py-1 rounded-full text-sm font-medium">
                    {job.type}
                  </span>
                </div>
                <div className="flex items-center text-gray-600 mb-4">
                  <MapPin className="w-4 h-4 mr-2" />
                  {job.location}
                </div>
                <Link
                  to="/contact"
                  className="inline-flex items-center text-primary-600 hover:text-primary-700 font-medium"
                >
                  Apply Now
                  <ArrowRight className="w-4 h-4 ml-1" />
                </Link>
              </motion.div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <h3 className="text-2xl font-bold text-gray-900 mb-6">Why Work With Us?</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="text-center">
                <Users className="w-12 h-12 text-primary-600 mx-auto mb-4" />
                <h4 className="text-lg font-semibold text-gray-900 mb-2">Great Culture</h4>
                <p className="text-gray-600">Collaborative environment focused on growth and innovation.</p>
              </div>
              <div className="text-center">
                <Shield className="w-12 h-12 text-primary-600 mx-auto mb-4" />
                <h4 className="text-lg font-semibold text-gray-900 mb-2">Excellent Benefits</h4>
                <p className="text-gray-600">Comprehensive health, retirement, and professional development benefits.</p>
              </div>
              <div className="text-center">
                <Target className="w-12 h-12 text-primary-600 mx-auto mb-4" />
                <h4 className="text-lg font-semibold text-gray-900 mb-2">Career Growth</h4>
                <p className="text-gray-600">Clear advancement paths and ongoing training opportunities.</p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-20 bg-primary-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <Target className="w-16 h-16 text-white mx-auto mb-8" />
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
              Our Mission
            </h2>
            <p className="text-xl text-blue-100 leading-relaxed">
              To empower our community's financial success through personalized banking solutions, 
              innovative technology, and unwavering commitment to exceptional service. We believe 
              that when our customers succeed, our community thrives.
            </p>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default About; 